import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { lazyWithRetry } from './common/utils/lazyWithRetry';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { PersistGate } from 'redux-persist/integration/react';
import { authGuard, routes, unAuthGuard } from './common/utils/routes';
import { storage } from './common/utils/storage';
import { useTokenRefresh } from './common/utils/tokenRefresh';
import LoadingView from './components/Loading/LoadingModal';
import PrivateRoute from './components/Route/PrivateRoute';
import PublicRoute from './components/Route/PublicRoute';
import OptionalAuthRoute from './components/Route/OptionalAuthRoute';
import store, { persistor } from './store';
import RewardGif from './components/RewardGif/RewardGif';
import ShowPoint from './components/ShowPoint/ModalPoint';

const MainPlatformView = lazyWithRetry(() => import('./views/MainPlatformView'));

const AuthView = lazyWithRetry(() => import('./views/auth/AuthView'));
const LoginForm = lazyWithRetry(() => import('./views/auth/components/login/LoginForm'));
const SignUpForm = lazyWithRetry(() => import('./views/auth/components/signup/SignUpForm'));
// ID찾기/PW찾기 비활성화 - Twilio OTP 미연결 상태
// const ForgotIdForm = lazy(() => import('./views/auth/components/forgot-id/ForgotIdForm'));
// const ForgotPasswordForm = lazy(() => import('./views/auth/components/forgot-password/ForgotPasswordForm'));
// const ResetPasswordView = lazy(() => import('./views/auth/components/reset-password/ResetPassword'));
const VerifyCodeView = lazyWithRetry(() => import('./views/auth/components/verify-code/VerifyCodeView'));
const VerifyAccountView = lazyWithRetry(() => import('./views/auth/components/verify-account/VerifyAccountView'));

const ProceedTask = lazyWithRetry(() => import('./views/proceed-task/ProceedTask'));
const SocialEditor = lazyWithRetry(() => import('./views/social-editor/SocialEditor'));
const ProjectView = lazyWithRetry(() => import('./views/dashboard/project/ProjectView'));
const CheckRewardView = lazyWithRetry(() => import('./views/dashboard/check-reward/CheckRewardView'));
const RewardExchangeView = lazyWithRetry(() => import('./views/dashboard/reward-exchange/RewardExchangeView'));
const MyPageView = lazyWithRetry(() => import('./views/dashboard/my-page/MyPageView'));
const CheckPasswordView = lazyWithRetry(() => import('./views/dashboard/my-page/CheckPasswordView'));

const DashboardView = lazyWithRetry(() => import('./views/dashboard/DashboardView'));
const AdminView = lazyWithRetry(() => import('./views/admin/AdminView'));
const CheckRoleView = lazyWithRetry(() => import('./views/CheckRoleView'));
const ProjectManagementView = lazyWithRetry(() => import('./views/admin/project-management/ProjectManagementView'));
const GTManagementView = lazyWithRetry(() => import('./views/admin/gt-management/GTManagementView'));
const ProceedGT = lazyWithRetry(() => import('./views/proceed-gt/ProceedTask2'));
const CreateCategoryView = lazyWithRetry(() => import('./views/admin/create-category/CreateCategoryView'));
const UserManagementView = lazyWithRetry(() => import('./views/admin/user-management/UserManagementView'));
const GroupManagementView = lazyWithRetry(() => import('./views/admin/group-management/GroupManagementView'));
const TaskReviewView = lazyWithRetry(() => import('./views/admin/task-review/TaskReviewView'));
const CreateUserView = lazyWithRetry(() => import('./views/admin/create-user/CreateUserView'));
const RewardManagementView = lazyWithRetry(() => import('./views/admin/reward-management/RewardManagementView'));
const RewardItemManagementView = lazyWithRetry(() => import('./views/admin/reward-item-management/RewardItemManagementView'));
const UserExchangeManagementView = lazyWithRetry(() => import('./views/admin/user-exchange-management/UserExchangeManagementView'));
const CreateRewardItemView = lazyWithRetry(() => import('./views/admin/create-reward-item/CreateRewardItemView'));

const CreateProjectView = lazyWithRetry(() => import('./views/admin/create-project/CreateProjectView'));
const CreateRewardView = lazyWithRetry(() => import('./views/admin/create-reward/CreateRewardView'));
const ProjectAnalyticsView = lazyWithRetry(() => import('./views/admin/project-analytics/ProjectAnalytics'));
const ReviewDashboardView = lazyWithRetry(() => import('./views/review/ReviewDashboard'));
const ReviewViewerView = lazyWithRetry(() => import('./views/review/ReviewViewer'));

// DreamChat View
const DreamChatView = lazyWithRetry(() => import('./views/dream-chat/DreamChatView'));

// Community Views
const CommunityView = lazyWithRetry(() => import('./views/community/CommunityView'));
const PostDetailView = lazyWithRetry(() => import('./views/community/PostDetailView'));
const PostWriteView = lazyWithRetry(() => import('./views/community/PostWriteView'));

const UploaderGalleryView = lazyWithRetry(() => import('./views/uploader/UploaderGalleryView'));

const UPLOADER_SLOT_PATHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 'dsu'] as const;

document.documentElement.lang = 'ko-KR';
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

const App: React.FC = () => {
  useTokenRefresh();

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <React.Suspense fallback={<LoadingView open />}>
            <BrowserRouter>
              <ToastContainer />
              <LoadingView />
              <RewardGif />
              <ShowPoint />
              <div className="container">
                <Routes>
                  {/* 홈 화면 - 누구나 접근 가능 */}
                  <Route
                    path={routes.MAIN}
                    element={<OptionalAuthRoute element={<MainPlatformView />} />}
                  />

                  {UPLOADER_SLOT_PATHS.map((n) => (
                    <Route
                      key={n}
                      path={routes.uploaderPath(n)}
                      element={<UploaderGalleryView slot={n} />}
                    />
                  ))}

                  {/* 커뮤니티 - 읽기는 Public, 쓰기는 Auth */}
                  <Route
                    path={routes.COMMUNITY}
                    element={<OptionalAuthRoute element={<CommunityView />} />}
                  />
                  <Route
                    path={routes.COMMUNITY_POST}
                    element={<OptionalAuthRoute element={<PostDetailView />} />}
                  />
                  <Route
                    path={routes.COMMUNITY_WRITE}
                    element={<PrivateRoute guards={[authGuard]} element={<PostWriteView />} />}
                  />
                  <Route
                    path={routes.COMMUNITY_EDIT}
                    element={<PrivateRoute guards={[authGuard]} element={<PostWriteView />} />}
                  />

                  <Route element={<AuthView />}>
                    <Route path={routes.LOGIN} element={<PrivateRoute guards={[unAuthGuard]} element={<LoginForm />} />} />
                    <Route path={routes.SIGN_UP} element={<PrivateRoute guards={[unAuthGuard]} element={<SignUpForm />} />} />
                    {/* ID찾기/PW찾기 라우트 비활성화
                    <Route path={routes.FORGOT_ID} element={<PrivateRoute guards={[unAuthGuard]} element={<ForgotIdForm />} />} />
                    <Route path={routes.FORGOT_PASSWORD} element={<PrivateRoute guards={[unAuthGuard]} element={<ForgotPasswordForm />} />} />
                    <Route path={routes.RESET_PASSWORD} element={<PrivateRoute guards={[unAuthGuard]} element={<ResetPasswordView />} />} />
                    */}
                    <Route path={routes.VERIFY_CODE} element={<PrivateRoute guards={[unAuthGuard]} element={<VerifyCodeView />} />} />
                    <Route path={routes.VERIFY_ACCOUNT} element={<PrivateRoute guards={[unAuthGuard]} element={<VerifyAccountView />} />} />
                  </Route>
                  {/* 꿈챗 */}
                  <Route
                    path={routes.DREAM_CHAT}
                    element={<PrivateRoute guards={[authGuard]} element={<DreamChatView />} />}
                  />
                  <Route
                    path={routes.DREAM_CHAT_DETAIL}
                    element={<PrivateRoute guards={[authGuard]} element={<DreamChatView />} />}
                  />

                  <Route path={routes.PROCEED_TASK} element={<PrivateRoute guards={[authGuard]} element={<ProceedTask />} />} />
                  <Route path={routes.SOCIAL_EDITOR} element={<PrivateRoute guards={[authGuard]} element={<SocialEditor />} />} />
                  <Route path={routes.PROCEED_GT} element={<PrivateRoute guards={[authGuard]} element={<ProceedGT />} />} />
                  {/* 기본 경로 - 로그인 상태에 따라 분기 */}
                  <Route
                    path={routes.DEFAULT}
                    element={<PublicRoute element={!storage.getToken() ? <AuthView /> : <CheckRoleView />} />}
                  />
                  <Route element={<PrivateRoute guards={[]} element={<DashboardView />} />}>
                    <Route path={routes.PROJECT} element={<PrivateRoute guards={[authGuard]} element={<ProjectView />} />} />
                    <Route path={routes.CHECK_REWARD} element={<PrivateRoute guards={[authGuard]} element={<CheckRewardView />} />} />
                    <Route path={routes.REWARD_EXCHANGE} element={<PrivateRoute guards={[authGuard]} element={<RewardExchangeView />} />} />
                    <Route path={routes.CHECK_PASSWORD} element={<PrivateRoute guards={[authGuard]} element={<CheckPasswordView />} />} />
                    <Route path={routes.MY_PAGE} element={<PrivateRoute guards={[authGuard]} element={<MyPageView />} />} />
                  </Route>
                  <Route element={<PrivateRoute guards={[]} element={<AdminView />} />}>
                    <Route
                      path={routes.PROJECT_MANAGEMENT}
                      element={<PrivateRoute guards={[authGuard]} element={<ProjectManagementView />} />}
                    />
                    <Route path={routes.CREATE_PROJECT} element={<PrivateRoute guards={[authGuard]} element={<CreateProjectView />} />} />
                    <Route path={routes.UPDATE_PROJECT} element={<PrivateRoute guards={[authGuard]} element={<CreateProjectView />} />} />
                    <Route path={routes.CREATE_CATEGORY} element={<PrivateRoute guards={[authGuard]} element={<CreateCategoryView />} />} />
                    <Route path={routes.USER_MANAGEMENT} element={<PrivateRoute guards={[authGuard]} element={<UserManagementView />} />} />
                    <Route path={routes.GT_MANAGEMENT} element={<PrivateRoute guards={[authGuard]} element={<GTManagementView />} />} />
                    <Route
                      path={routes.REWARD_MANAGEMENT}
                      element={<PrivateRoute guards={[authGuard]} element={<RewardManagementView />} />}
                    />
                    <Route path={routes.CREATE_REWARD} element={<PrivateRoute guards={[authGuard]} element={<CreateRewardView />} />} />
                    <Route
                      path={routes.USER_EXCHANGE_MANAGEMENT}
                      element={<PrivateRoute guards={[authGuard]} element={<UserExchangeManagementView />} />}
                    />
                    <Route
                      path={routes.REWARD_ITEM_MANAGEMENT}
                      element={<PrivateRoute guards={[authGuard]} element={<RewardItemManagementView />} />}
                    />
                    <Route
                      path={routes.CREATE_REWARD_ITEM}
                      element={<PrivateRoute guards={[authGuard]} element={<CreateRewardItemView />} />}
                    />
                    <Route
                      path={routes.UPDATE_REWARD_ITEM}
                      element={<PrivateRoute guards={[authGuard]} element={<CreateRewardItemView />} />}
                    />
                    <Route path={routes.UPDATE_REWARD} element={<PrivateRoute guards={[authGuard]} element={<CreateRewardView />} />} />
                    <Route
                      path={routes.GROUP_MANAGEMENT}
                      element={<PrivateRoute guards={[authGuard]} element={<GroupManagementView />} />}
                    />
                    <Route path={routes.CREATE_USER} element={<PrivateRoute guards={[authGuard]} element={<CreateUserView />} />} />
                    <Route path={routes.UPDATE_USER} element={<PrivateRoute guards={[authGuard]} element={<CreateUserView />} />} />
                    <Route path={routes.PROJECT_ANALYTICS} element={<PrivateRoute guards={[authGuard]} element={<ProjectAnalyticsView />} />} />
                    <Route path={routes.REVIEW_DASHBOARD} element={<PrivateRoute guards={[authGuard]} element={<ReviewDashboardView />} />} />
                    <Route path={routes.REVIEW_VIEWER} element={<PrivateRoute guards={[authGuard]} element={<ReviewViewerView />} />} />
                  </Route>
                  <Route path={routes.TASK_REVIEW} element={<PrivateRoute guards={[authGuard]} element={<TaskReviewView />} />} />
                </Routes>
              </div>
            </BrowserRouter>
          </React.Suspense>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
};

export default App;
